/**
 * A lane-v2 ask in the conversation — THE DECISION CARD. Pending: the EXISTING `InteractionCard`
 * (one card for both lanes) with options as one-click actions and, inside it, what is being decided
 * (the plan's goal and items for a plan ask; the package in one line for a changeset/apply ask).
 * Settled: one line under the turn that raised it ("You chose: Region__c") — only for a question
 * with no task; a task's settled asks are rows of its story (`presentation.historyFor`).
 *
 * Free text is the composer: a reply typed below answers an open approval card on the server
 * (`bind_reply` executes `request_changes`), so the card says so instead of growing a textarea.
 * Answers go through the caller's `onAnswerAsk` (the store's `answerAsk`: the one respond-target
 * seam plus `askRouting.ts`). A stale refusal re-reads, never re-sends.
 */
import { useCallback, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { InteractionCard } from "@/components/organisms/multi-agent/interaction/InteractionCard";
import { describeRespondFailure } from "@/services/multi-agent/interactionCopy";
import type { RespondBody } from "@/services/multi-agent/interactions";
import { LaneV2Error } from "@/services/multi-agent/laneV2/api";
import { isForwardDeployAsk, offlineRefusesAnswer } from "@/services/multi-agent/laneV2/conversation";
import { askRecordTone, askRecordWords } from "@/services/multi-agent/laneV2/presentation";
import type { AskView, ConnectorWriteOutcome, TaskSnapshot } from "@/services/multi-agent/laneV2/types";
import { LaneV2PackageSummary, LaneV2PlanSummary } from "./LaneV2PlanSummary";
import { PlanQuestionsCard } from "./PlanQuestionsCard";

export interface LaneV2AskBlockProps {
  ask: AskView;
  org?: { is_production?: boolean; org_name?: string } | null;
  offline: boolean;
  /** The task the ask is about, for the summary inside the card. */
  snapshot?: TaskSnapshot | null;
  /** Open the workspace Task section on the ask's task — "View full plan" / "Review changes". */
  onOpenTask?: (taskId: string, openedBy?: "auto" | "user") => void;
  /** Open one component's diff (a plan name that is already in the package). */
  onOpenItem?: (taskId: string, workItemId: string) => void;
  /** Throws the typed error on refusal — this block turns it into copy. */
  onAnswerAsk: (ask: AskView, body: RespondBody) => Promise<void>;
  /** Re-read the asks and the ask's task after a stale refusal (never an automatic retry). */
  onStaleAsk: (ask: AskView) => void;
  /** A message of the person's is being worked on (an answer revising this plan): the card waits and cannot be answered. */
  updating?: boolean;
  /** Send a chat message: how an open question on the plan is answered (the server revises the plan from it). */
  onSendAnswer?: (text: string) => void;
}

const PLAN_SUBJECTS: ReadonlySet<string> = new Set(["plan_revision"]);
const PACKAGE_SUBJECTS: ReadonlySet<string> = new Set(["package", "apply", "revert"]);

/**
 * Plan cards whose full plan was already opened beside the chat, once each (dev 2026-11-02: the person wants the plan in the right panel
 * while deciding). Module-wide so a re-render, a re-mount or the person closing the panel never re-opens it; a REVISED plan is a new
 * card and opens again, so the panel shows the version being decided.
 */
const planPanelOpened = new Set<string>();
/** Only where the panel fits beside the conversation without covering it. */
const PANEL_FITS = "(min-width: 1280px)";
/** How long a plan card waits for its plan before falling back to the ordinary card. */
const PLAN_WAIT_MS = 15_000;

/**
 * Should this card open the full plan beside the chat now? A pending plan card, not mid-update, with a task and a way to open it,
 * on a screen where the panel fits, and only the first time for this card. (The effect below is its only caller; this repo tests
 * effects through their pure decision, as `ArtifactSection.test.tsx` documents.)
 */
export function shouldOpenPlanPanel(a: { planAsk: boolean; updating: boolean; taskId: string | null; canOpen: boolean; askId: string; opened: ReadonlySet<string>; fits: boolean }): boolean {
  return a.planAsk && !a.updating && !!a.taskId && a.canOpen && a.fits && !a.opened.has(a.askId);
}

/** The approve button while the plan still has open questions: approving skips them, so the label says so. */
export const APPROVE_AS_IS = "Approve as is";

/** React 18 passes `inert` through only as a string attribute; it keeps a waiting card out of the tab order, not just the mouse. */
const INERT = { inert: "" } as Record<string, string>;

/** Relabel the approve option while open questions stand (dev 2026-11-02: "Approve" read as answering the question too). */
export function withApproveAsIs<T extends { options: { option_id: string; label: string }[] }>(interaction: T): T {
  return { ...interaction, options: interaction.options.map((o) => (o.option_id === "approve" ? { ...o, label: APPROVE_AS_IS } : o)) };
}

/** The plan card's one heading; the card's own eyebrow is this same sentence, so it is said once. */
export const PLAN_HEADING = "Approve this plan?";

/**
 * A plan ask's server prompt opens with "Approve plan version 1 (2 items)?" — said again under the card's "Approve this plan?" eyebrow
 * (dev 2026-11-02), with an internal version number and the items the summary below already lists. The first line becomes the one
 * heading; every later line (NOT INCLUDED, irreversible notes) is kept exactly as the server wrote it.
 */
export function withPlanHeading<T extends { prompt: string }>(interaction: T): T {
  const rest = interaction.prompt.split("\n").slice(1).join("\n");
  return { ...interaction, prompt: rest ? `${PLAN_HEADING}\n${rest}` : PLAN_HEADING };
}

export function LaneV2AskBlock({ ask, org, offline, snapshot, onOpenTask, onOpenItem, onAnswerAsk, onStaleAsk, updating = false, onSendAnswer }: LaneV2AskBlockProps) {
  const [failure, setFailure] = useState<ReturnType<typeof describeRespondFailure> | null>(null);
  const onRespond = useCallback(
    async (body: RespondBody) => {
      setFailure(null);
      /*
       * `"approve"` is the only option_id the server ever gates offline (see `offlineRefusesAnswer`'s
       * own doc comment for the exact call sites). Refusing here, before ever calling the server,
       * shows the identical LANE_V2_OFFLINE copy instantly instead of after a round trip that would
       * refuse it anyway; a decline, a park ask's "ack" and free text are never gated.
       */
      if (offlineRefusesAnswer(offline, body.option_id)) {
        setFailure(describeRespondFailure(new LaneV2Error(503, "LANE_V2_OFFLINE", "", null, null)));
        return;
      }
      try {
        await onAnswerAsk(ask, body);
      } catch (err) {
        const described = describeRespondFailure(err);
        setFailure(described);
        // A stale card is re-read — never re-sent. The user looks at the new proposal first.
        if (described.staleSubject) onStaleAsk(ask);
      }
    },
    [ask, offline, onAnswerAsk, onStaleAsk],
  );
  const subject = ask.interaction.subject?.type ?? null;
  const taskId = ask.task_id;
  const openTask = taskId && onOpenTask ? () => onOpenTask(taskId) : undefined;
  const askId = ask.interaction.interaction_id;
  const planAsk = !!subject && PLAN_SUBJECTS.has(subject) && !!snapshot?.plan;
  useEffect(() => {
    // the full plan opens beside the chat when it is put up for approval — once per card, and only where it fits
    const fits = typeof window !== "undefined" && !!window.matchMedia?.(PANEL_FITS).matches;
    if (!shouldOpenPlanPanel({ planAsk, updating, taskId, canOpen: !!onOpenTask, askId, opened: planPanelOpened, fits })) return;
    planPanelOpened.add(askId);
    // "auto": the reader did not click, so focus stays in the composer and the code view may replace it when the build lands
    onOpenTask!(taskId!, "auto");
  }, [planAsk, updating, taskId, onOpenTask, askId]);
  // The ask can arrive a few seconds before the task's plan (R3 snapshot) does. Until it lands, a plan card shows a quiet loading line
  // rather than the server's raw approval prompt (dev 2026-11-02: "CHANGE SHAPE ... rules coverage_custom_field" for 1-6 s on every
  // plan). If the plan never arrives, the ordinary card comes back after PLAN_WAIT_MS so the decision is never blocked.
  const waitingForPlan = !!subject && PLAN_SUBJECTS.has(subject) && !snapshot?.plan && ask.status === "pending";
  const [planWaitOver, setPlanWaitOver] = useState(false);
  useEffect(() => {
    if (!waitingForPlan) return;
    const t = window.setTimeout(() => setPlanWaitOver(true), PLAN_WAIT_MS);
    return () => window.clearTimeout(t);
  }, [waitingForPlan]);
  const summary =
    snapshot && subject && PLAN_SUBJECTS.has(subject) && snapshot.plan ? (
      <LaneV2PlanSummary
        plan={snapshot.plan}
        snapshot={snapshot}
        onOpenItem={onOpenItem}
        onOpenFull={openTask}
      />
    ) : snapshot && subject && PACKAGE_SUBJECTS.has(subject) && snapshot.package ? (
      <LaneV2PackageSummary pkg={snapshot.package} onReview={openTask} />
    ) : null;
  // A forward task's approve_apply is a retired card (see `isForwardDeployAsk`); the transcript filters these out too.
  if (isForwardDeployAsk(ask, snapshot)) return null;
  if (waitingForPlan && !planWaitOver && onSendAnswer && !updating) {
    return (
      <div data-lane-v2-ask={ask.interaction.interaction_id} data-lane-v2-plan-loading="" className="border-l-2 border-l-primary py-1 pl-3.5">
        <p className="text-[14.5px] font-semibold leading-snug text-foreground">Approve this plan?</p>
        <p role="status" className="mt-1.5 flex items-center gap-2 text-[13px] text-muted-foreground">
          <span aria-hidden className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-[var(--ma-accent-text)]" />
          Loading the plan…
        </p>
      </div>
    );
  }
  const isPlan = !!summary && !!subject && PLAN_SUBJECTS.has(subject);
  const openQuestions = isPlan && (snapshot?.plan?.open_questions?.length ?? 0) > 0;
  const shown = isPlan ? (openQuestions ? withApproveAsIs(withPlanHeading(ask.interaction)) : withPlanHeading(ask.interaction)) : ask.interaction;
  // A plan is decided from one numbered list — Approve, Decline, Something else (a text box in the row) — with Weave's open questions
  // shown above it. While a message of the person's is being worked on, or with no way to send one, the ordinary card stays (dimmed
  // while updating).
  if (isPlan && onSendAnswer && !updating && snapshot?.plan) {
    return (
      <div data-lane-v2-ask={ask.interaction.interaction_id}>
        <PlanQuestionsCard
          questions={snapshot.plan.open_questions ?? []}
          plan={<LaneV2PlanSummary plan={snapshot.plan} snapshot={snapshot} onOpenItem={onOpenItem} onOpenFull={openTask} hideQuestions />}
          onSend={onSendAnswer}
          onChoose={(option_id) => onRespond({ option_id })}
          error={failure?.message || null}
        />
      </div>
    );
  }
  return (
    <div data-lane-v2-ask={ask.interaction.interaction_id} aria-busy={updating || undefined}>
      {updating && (
        <p className="mb-1.5 flex items-center gap-2 text-[12.5px] font-medium text-muted-foreground" data-lane-v2-ask-updating="">
          <span aria-hidden className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-[var(--ma-accent-text)]" />
          Updating the plan with your message. This card will be replaced when the new plan is ready.
        </p>
      )}
      <div className={cn(updating && "pointer-events-none select-none opacity-50")} {...(updating ? INERT : {})}>
      <InteractionCard
        interaction={shown}
        org={org}
        onRespond={onRespond}
        promptLayout={summary ? "summary" : "full"}
        owner="task"
        optionsAsActions
        error={failure?.message || null}
      >
        {summary}
      </InteractionCard>
      </div>
    </div>
  );
}

/**
 * A settled task-less ask (a clarify, an approved connector write), kept under its turn as one line in the story's words.
 * `outcome` (MSA leg `cv-connector-writes`): what the approved write did, when the client has it — the line says written
 * or refused instead of only "approved", a refusal reads as a warning, and a written record gets its link.
 */
export function LaneV2AskRecord({ ask, outcome = null }: { ask: AskView; outcome?: ConnectorWriteOutcome | null }) {
  const refused = outcome !== null && !outcome.ok;
  return (
    <p
      className={cn("border-l-2 py-0.5 pl-3 text-[12px]", refused ? "border-ma-warn-line text-ma-warn-text" : "border-border text-muted-foreground")}
      data-lane-v2-ask-record={ask.interaction.interaction_id}
      data-lane-v2-write-outcome={outcome ? (outcome.ok ? "ok" : "refused") : undefined}
      data-tone={askRecordTone(ask, outcome)}
    >
      {askRecordWords(ask, outcome)}
      {outcome?.ok && outcome.url && (
        <>
          {" "}
          <a
            href={outcome.url}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary underline-offset-2 hover:underline"
            data-lane-v2-write-link=""
          >
            Open in {outcome.provider || "the connected tool"}
          </a>
        </>
      )}
    </p>
  );
}

const PLAN_ASK_KINDS: ReadonlySet<string> = new Set(["approve_plan", "approve_plan_revision"]);

/** The newest APPROVED plan ask of one task (a declined or replaced one is not "the plan you approved"), else null. */
export function approvedPlanAsk(asks: readonly AskView[], taskId: string | null): AskView | null {
  if (!taskId) return null;
  const approved = asks.filter((a) => {
    if (a.task_id !== taskId || a.status !== "answered" || !PLAN_ASK_KINDS.has(a.interaction.kind)) return false;
    const r = a.resolution?.resolution;
    return r?.option_id === "approve" || r?.approved === true;
  });
  return approved.length ? approved[approved.length - 1] : null;
}

/** Is a plan (or plan-revision) ask of this task still waiting for an answer? */
export function planAskPending(asks: readonly AskView[], taskId: string): boolean {
  return asks.some((a) => a.task_id === taskId && a.status === "pending" && PLAN_ASK_KINDS.has(a.interaction.kind));
}

/**
 * The settled plan ask, collapsed: "You approved the plan" opens to the plan itself (`LaneV2PlanSummary`), so the reader
 * can see what they agreed to without a trip to the workspace. The plan shown is the task's current revision.
 */
export function LaneV2ApprovedPlan({ ask, snapshot, onOpenTask, onOpenItem }: { ask: AskView; snapshot: TaskSnapshot; onOpenTask?: (taskId: string) => void; onOpenItem?: (taskId: string, workItemId: string) => void }) {
  if (!snapshot.plan) return <LaneV2AskRecord ask={ask} />;
  const taskId = snapshot.task_id;
  return (
    <details className="group border-l-2 border-border pl-3 text-[12px]" data-lane-v2-approved-plan={ask.interaction.interaction_id}>
      <summary className="cursor-pointer list-none py-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <span aria-hidden className="mr-1 inline-block transition-transform group-open:rotate-90">▸</span>
        {askRecordWords(ask)}
      </summary>
      <div className="mt-1.5">
        <LaneV2PlanSummary plan={snapshot.plan} snapshot={snapshot} onOpenItem={onOpenItem} onOpenFull={onOpenTask ? () => onOpenTask(taskId) : undefined} />
      </div>
    </details>
  );
}
